import { Injectable, computed, signal } from '@angular/core';
import { CartItem, Product, salePrice } from '../models/product.model';

const STORAGE_KEY = 'hnh_jewellery_cart';

@Injectable({ providedIn: 'root' })
export class CartService {
  private readonly itemsSignal = signal<CartItem[]>(this.loadFromStorage());

  readonly items = this.itemsSignal.asReadonly();

  readonly itemCount = computed(() =>
    this.itemsSignal().reduce((sum, item) => sum + item.quantity, 0)
  );

  readonly total = computed(() =>
    this.itemsSignal().reduce(
      (sum, item) => sum + salePrice(item.price, item.discount) * item.quantity,
      0
    )
  );

  addItem(product: Product, quantity = 1): void {
    const items = this.itemsSignal();
    const existing = items.find((i) => i.id === product.id);

    if (existing) {
      this.updateQuantity(product.id, existing.quantity + quantity);
      return;
    }

    const item: CartItem = {
      id: product.id,
      name: product.name,
      slug: product.slug,
      price: product.price,
      discount: product.discount,
      thumbnail_url: product.thumbnail_url || product.images[0] || '',
      quantity,
    };

    this.itemsSignal.set([...items, item]);
    this.persist();
  }

  removeItem(productId: number): void {
    this.itemsSignal.set(this.itemsSignal().filter((i) => i.id !== productId));
    this.persist();
  }

  updateQuantity(productId: number, quantity: number): void {
    if (quantity < 1) {
      this.removeItem(productId);
      return;
    }

    this.itemsSignal.set(
      this.itemsSignal().map((i) =>
        i.id === productId ? { ...i, quantity } : i
      )
    );
    this.persist();
  }

  clear(): void {
    this.itemsSignal.set([]);
    this.persist();
  }

  private persist(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(this.itemsSignal())
    );
  }

  private loadFromStorage(): CartItem[] {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CartItem[]) : [];
    } catch {
      return [];
    }
  }
}
